import { useEffect } from "react";
import {
  ArrowRight,
  Check,
  FileText,
  Filter,
  ListChecks,
  MessageSquare,
  Phone,
  Play,
  Scale,
  Users,
  Compass,
  Video,
  X,
} from "lucide-react";
import { bookingLinkProps } from "@/pages/apex25/Apex25Chrome";
import { APEX25_INDUSTRY_PAGES } from "@/pages/apex25/industries";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import "./legal-industry.css";

const META = APEX25_INDUSTRY_PAGES["/for-legal"];

const steps = [
  {
    title: "Set the criteria for your role",
    body: "Define the qualifications, experience, and other role requirements you want to assess.",
  },
  {
    title: "Review and rank CVs",
    body: "Evaluate applications against those criteria to help organise your review and identify candidates to explore further.",
  },
  {
    title: "Invite candidates to a first-round AI interview",
    body: "Choose whom to invite and the role-specific questions you want to explore beyond their CV.",
  },
  {
    title: "Review the responses and feedback",
    body: "Examine completed interviews, transcripts, and feedback to understand candidates’ answers and identify areas for follow-up.",
  },
  {
    title: "Choose who moves forward",
    body: "Bring the available information together and decide which candidates to meet. Your team makes the decision.",
  },
];

const faqs = [
  {
    question: "Does Apex 25 make the hiring decision?",
    answer:
      "No. Apex 25 supports application review and initial screening. Your team chooses whom to interview and who progresses.",
  },
  {
    question: "Can we use our own hiring criteria?",
    answer:
      "You can define criteria for the role and evaluate CVs against those requirements. Your team also sets the direction for the first-round interview.",
  },
  {
    question: "Does every applicant need to complete an AI interview?",
    answer:
      "No. Your team selects whom to invite. Interview responses and feedback become available after a candidate completes the interview.",
  },
  {
    question: "Does AI remove bias from recruitment?",
    answer:
      "No tool can guarantee a bias-free hiring process. Apex 25 supports review against defined criteria, while your team remains responsible for checking results, considering missing context, and making decisions.",
  },
];

export const LegalIndustryPage = () => {
  useEffect(() => {
    const previousTitle = document.title;
    // The server sends the full <head> on a page load (apps/server/src/brand-sites); this keeps the
    // tab title and description right after in-app navigation.
    document.title = META.title;
    const existingDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const description = existingDescription ?? document.createElement("meta");
    const previousDescription = description.getAttribute("content");
    description.name = "description";
    description.content = META.description;
    if (!existingDescription) document.head.appendChild(description);

    return () => {
      document.title = previousTitle;
      if (!existingDescription) description.remove();
      else if (previousDescription === null) description.removeAttribute("content");
      else description.content = previousDescription;
    };
  }, []);

  return (
    <div className="legal-industry">
      <section className="legal-hero" aria-labelledby="legal-heading">
        <div className="legal-shell legal-hero-grid">
          <div>
            <p className="legal-eyebrow">
              <Scale size={17} aria-hidden="true" /> For legal HR and recruitment teams
            </p>
            <h1 id="legal-heading">
              Spend less time screening CVs.
              <span>Gather more data to make your next hire your best hire.</span>
            </h1>
            <p className="legal-lead">
              Screen resumes, run structured AI interviews, and rank candidates objectively — with
              humans in control of every call.
            </p>
            <div className="legal-actions">
              <a className="legal-button legal-button-primary" {...bookingLinkProps("legal")}>
                Book a demo <ArrowRight size={17} aria-hidden="true" />
              </a>
              <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="legal-button legal-button-secondary">
                    <Play size={16} aria-hidden="true" /> Watch demo
                  </button>
                </DialogTrigger>
                <DialogContent className="legal-demo-dialog">
                  <div className="flex items-center justify-between gap-4">
                    <DialogTitle>Inside Apex 25</DialogTitle>
                    <DialogClose className="rounded p-2" aria-label="Close product walkthrough">
                      <X size={20} />
                    </DialogClose>
                  </div>
                  <DialogDescription>
                    General product walkthrough. Interface and availability may vary by plan.
                  </DialogDescription>
                  <video
                    controls
                    playsInline
                    preload="metadata"
                    src="https://d1ukqlfbhdwy5x.cloudfront.net/videos/therecruit-hero.mp4"
                    aria-label="Apex 25 product walkthrough"
                  />
                </DialogContent>
              </Dialog>
            </div>
            <p className="legal-reassurance">
              <Check size={16} aria-hidden="true" /> Your team sets the criteria and decides who
              moves forward.
            </p>
          </div>
          <div
            className="legal-video-placeholder"
            role="img"
            aria-label="Interview video placeholder"
          >
            <Video size={40} strokeWidth={1.25} aria-hidden="true" />
            <span>Interview video</span>
            <small>Placeholder</small>
          </div>
        </div>
        <div className="legal-shell legal-role-band">
          <span>Support for your junior legal hiring</span>
          <ul aria-label="Example roles">
            <li>Graduates</li>
            <li>Paralegals</li>
            <li>Junior legal roles</li>
          </ul>
        </div>
      </section>

      <section className="legal-section" aria-labelledby="legal-problem-heading">
        <div className="legal-shell">
          <div className="legal-problem-overview">
            <h2 id="legal-problem-heading">
              Too many applications,
              <br />
              too little time.
            </h2>
            <ul className="legal-problem-bullets">
              <li>CVs piling up.</li>
              <li>Too many potential options.</li>
              <li>Hiring deadline fast approaching.</li>
            </ul>
          </div>
          <p className="legal-options-label" id="legal-options-label">
            Your options today
          </p>
          <ul className="legal-current-options" aria-labelledby="legal-options-label">
            {[
              { icon: Users, title: "Delegate to a junior", detail: "First-round CV review" },
              { icon: Filter, title: "ATS auto-reject", detail: "Apply hard filters" },
              { icon: Phone, title: "Screen or assess", detail: "Phone screening or skills tests" },
              { icon: Compass, title: "Gut feel", detail: "Go with your instinct" },
            ].map(({ icon: Icon, title, detail }) => (
              <li key={title}>
                <div className="legal-option-icon">
                  <Icon size={24} aria-hidden="true" />
                </div>
                <h3>{title}</h3>
                <p>{detail}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="legal-section legal-solution" aria-labelledby="legal-solution-heading">
        <div className="legal-shell">
          <div className="legal-split">
            <div>
              <p className="legal-eyebrow">Support for your HR team</p>
              <h2 id="legal-solution-heading">
                Another set of hands. <br />
                <span>More information for your shortlist.</span>
              </h2>
            </div>
            <ul className="legal-capabilities">
              <li>
                <Check size={19} aria-hidden="true" />
                <span>Rank CVs against other candidates for your job description.</span>
              </li>
              <li>
                <Check size={19} aria-hidden="true" />
                <span>Invite candidates to an automated, real-time AI interview.</span>
              </li>
              <li>
                <Check size={19} aria-hidden="true" />
                <span>Get evidence-based feedback on each candidate’s AI interview.</span>
              </li>
            </ul>
          </div>
          <div className="legal-screenshot-grid">
            {[
              { icon: ListChecks, title: "Candidate profile after ranking" },
              { icon: Video, title: "AI interview in progress" },
              { icon: FileText, title: "Completed interview" },
              { icon: MessageSquare, title: "AI answers based on interview evidence" },
            ].map(({ icon: Icon, title }, index) => (
              <figure className="legal-screenshot" key={title}>
                <div
                  className="legal-screenshot-placeholder"
                  role="img"
                  aria-label={`${title} — screenshot placeholder`}
                >
                  <Icon size={36} strokeWidth={1.25} aria-hidden="true" />
                  <span>Screenshot placeholder</span>
                </div>
                <figcaption>
                  <span className="legal-screenshot-number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {title}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="legal-section" aria-labelledby="legal-workflow-heading">
        <div className="legal-shell legal-split">
          <div>
            <p className="legal-eyebrow">How it works</p>
            <h2 id="legal-workflow-heading">
              From applications to a shortlist your team can explain.
            </h2>
            <p className="legal-section-intro">
              A first review, a deeper conversation, and a decision that stays with you.
            </p>
          </div>
          <ol className="legal-steps">
            {steps.map((step, index) => (
              <li key={step.title}>
                <span className="legal-step-number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="legal-section legal-value" aria-labelledby="legal-value-heading">
        <div className="legal-shell">
          <p className="legal-eyebrow">Beyond the application</p>
          <h2 id="legal-value-heading">
            Give your shortlist more than
            <br />
            academic results to stand on.
          </h2>
          <div className="legal-value-intro legal-prose">
            <p>
              Academic achievement and relevant experience matter. So does understanding what a
              candidate contributed, how they explain their reasoning, and where you need to ask
              more.
            </p>
            <p>
              Candidate responses give your team additional information to consider alongside the
              CV—helping you prepare a more focused interview and explain why someone belongs on
              your shortlist.
            </p>
          </div>
          <div className="legal-benefits">
            <div>
              <FileText aria-hidden="true" />
              <h3>Less manual screening</h3>
              <p>Use CV ranking to help organise applications around your role requirements.</p>
            </div>
            <div>
              <MessageSquare aria-hidden="true" />
              <h3>More context before you meet</h3>
              <p>Explore candidates’ experience through completed first-round interviews.</p>
            </div>
            <div>
              <ListChecks aria-hidden="true" />
              <h3>A more consistent review</h3>
              <p>
                Set role-specific criteria and questions, then review the available evidence with
                your team.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="legal-section" aria-labelledby="legal-faq-heading">
        <div className="legal-shell legal-split">
          <div>
            <p className="legal-eyebrow">Your questions, answered</p>
            <h2 id="legal-faq-heading">Before you get started.</h2>
          </div>
          <div className="legal-faq">
            {faqs.map((faq) => (
              <details key={faq.question}>
                <summary>
                  {faq.question}
                  <span aria-hidden="true">+</span>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="legal-final-cta" aria-labelledby="legal-cta-heading">
        <div className="legal-shell">
          <p className="legal-eyebrow">Your next legal hire</p>
          <h2 id="legal-cta-heading">
            Spend less time working through applications.
            <br />
            <span>More time getting to know your next legal hire.</span>
          </h2>
          <p>
            See how Apex 25 can support your team’s application review and first-round screening
            for junior legal roles.
          </p>
          <a className="legal-button legal-button-primary" {...bookingLinkProps("legal")}>
            Book a demo <ArrowRight size={17} aria-hidden="true" />
          </a>
        </div>
      </section>
    </div>
  );
};
